"use client"

import type React from "react"
import "../../../styles/admin-buttons.css"

import { useCallback, useEffect, useState } from "react"
import { Row, Col, Card, Button, Table, Badge, Form, Modal, Alert, Spinner, Dropdown, Container } from "react-bootstrap"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { fetchUsers, approveUser, updateUserRole, deleteUser, enableUser } from "../../../redux/slices/usersSlice"
import { fetchBuildings } from "../../../redux/slices/buildingsSlice"
import { compareBuildingNames } from "../../../data/buildings"
import { useTranslation } from "../../../hooks/use-translation"
import { User, UserCheck, UserPlus, Trash2, Edit, Search, Upload, Settings, Filter } from "lucide-react"
import { useToast } from "../../../components/ui/use-toast"
import { updateUserProfile } from "../../../redux/slices/usersSlice"
import { validateAndResizeImage, getImageValidationLimits } from "../../../utils/image-validation"
import type { User as UserType } from "../../../contexts/auth-context"
import apiClient from "../../../api/apiClient"
import { getTranslatedTicketCategories } from "../../../constants/ticket-categories"
import {
  fetchSoftoneUserLinks,
  saveSoftoneUserLink,
  linkToSelectValue,
  selectValueToChoice,
} from "../../../api/softone"
import { softoneSearchKey, softoneUnitNumber, type SoftoneApartment, type SoftoneUserLinkItem } from "../../../data/softone"
import { SoftoneLinkBadge, SoftoneLinkSelect } from "../../../components/admin/softone/SoftoneLink"

type SoftoneFilter = "all" | "linked" | "suggested" | "pending" | "none" | "stale"

const softoneFilterLabels: Record<SoftoneFilter, string> = {
  all: "softoneFilterAll",
  linked: "softoneFilterLinked",
  suggested: "softoneFilterSuggested",
  pending: "softoneFilterPending",
  none: "softoneFilterNone",
  stale: "softoneFilterStale",
}

export default function AdminDashboardPage() {
  const dispatch = useAppDispatch()
  const { users = [], isLoading } = useAppSelector((state) => state.users)
  const { buildings = [], isLoading: buildingsLoading } = useAppSelector((state) => state.buildings)
  const { t } = useTranslation()
  const { toast } = useToast()
  const [filter, setFilter] = useState("all") // all, pending, approved, disabled, duplicates
  const [buildingFilter, setBuildingFilter] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [sortField, setSortField] = useState<"name" | "email" | "roomNumber" | null>(null)
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const [showEditModal, setShowEditModal] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null)
  const [editForm, setEditForm] = useState({
    fullName: "",
    email: "",
    building: "",
    roomNumber: "",
    phoneNumber: "",
    role: "user",
    canReceiveMessages: true,
    muteGuestNotifications: false,
    disableEmailNotifications: false,
    managedBuildings: [] as string[],
    managedTicketCategories: [] as string[],
  })
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; userId: string; userName: string }>({
    show: false,
    userId: "",
    userName: "",
  })
  const [registrationCode, setRegistrationCode] = useState("")
  const [isLoadingSettings, setIsLoadingSettings] = useState(false)
  const [isSavingSettings, setIsSavingSettings] = useState(false)

  // SoftOne links (admin only; loaded separately because SoftOne can be slow)
  const [softoneItems, setSoftoneItems] = useState<Record<string, SoftoneUserLinkItem>>({})
  const [softoneApartments, setSoftoneApartments] = useState<SoftoneApartment[]>([])
  const [softoneBuildingLinks, setSoftoneBuildingLinks] = useState<Record<string, string | null>>({})
  const [softoneLoading, setSoftoneLoading] = useState(false)
  const [softoneError, setSoftoneError] = useState<string | null>(null)
  const [softoneFilter, setSoftoneFilter] = useState<SoftoneFilter>("all")
  const [approvingSoftoneUserId, setApprovingSoftoneUserId] = useState<string | null>(null)
  const [softoneValue, setSoftoneValue] = useState("")
  const [softoneInitialValue, setSoftoneInitialValue] = useState("")

  const { maxFileSizeKB, maxDimension } = getImageValidationLimits()

  const ticketCategories = getTranslatedTicketCategories(t)

  useEffect(() => {
    dispatch(fetchUsers({ status: filter }))
    dispatch(fetchBuildings())
  }, [dispatch, filter])

  const loadSoftoneLinks = useCallback(async () => {
    setSoftoneLoading(true)
    setSoftoneError(null)
    try {
      const data = await fetchSoftoneUserLinks()
      setSoftoneItems(Object.fromEntries(data.items.map((item) => [item.userId, item])))
      setSoftoneApartments(data.apartments)
      setSoftoneBuildingLinks(data.buildingLinks)
    } catch (error: any) {
      console.error("Error loading SoftOne links:", error)
      setSoftoneError(error.response?.data?.error || t("softoneLoadError"))
    } finally {
      setSoftoneLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSoftoneLinks()
  }, [loadSoftoneLinks])

  const handleApproveSoftoneSuggestion = async (userId: string, softonePrjc: string) => {
    setApprovingSoftoneUserId(userId)
    try {
      const link = await saveSoftoneUserLink(userId, { status: "linked", softonePrjc })
      setSoftoneItems((prev) => ({ ...prev, [userId]: { ...prev[userId], link, stale: false } }))
      toast({ title: t("success"), description: t("softoneLinkSaved") })
    } catch (error: any) {
      toast({
        title: t("error"),
        description: error.response?.data?.error || t("softoneLinkSaveError"),
        variant: "destructive",
      })
    } finally {
      setApprovingSoftoneUserId(null)
    }
  }

  const softoneNote = (item: SoftoneUserLinkItem | undefined) => {
    if (!item) return ""
    if (!item.softoneBuildingPrjc) {
      return item.note === "building has no SoftOne link" ? t("softoneBuildingNoLink") : t("softoneBuildingNotLinked")
    }
    return item.note
  }

  // Popover rows for a SoftOne apartment: the contract period ("2026-10-01" → "01/10/2026")
  const softoneApartmentDetails = (prjc: string) => {
    const apartment = softoneApartments.find((a) => a.prjc === prjc)
    if (!apartment?.fromdate && !apartment?.finaldate) return []
    const day = (date: string | null) => (date ? date.slice(0, 10).split("-").reverse().join("/") : "…")
    return [{ label: t("softoneDetailContract"), value: `${day(apartment.fromdate)} – ${day(apartment.finaldate)}` }]
  }

  const softoneAdminName = (uid: string) => users.find((u) => u.id === uid)?.fullName

  // Apartment options for the edit modal: the SoftOne building linked to the building currently selected in the form
  const softoneApartmentOptions = softoneApartments
    .filter((a) => a.buildingPrjc === softoneBuildingLinks[editForm.building])
    .map((a) => ({ value: a.prjc, label: softoneUnitNumber(a.code) }))

  const loadRegistrationSettings = async () => {
    setIsLoadingSettings(true)
    try {
      const response = await apiClient.get("/settings/registration")
      setRegistrationCode(response.data.registrationCode || "")
    } catch (error: any) {
      console.error("Error loading registration settings:", error)
      toast({
        title: t("error"),
        description: "Failed to load registration settings",
        variant: "destructive",
      })
    } finally {
      setIsLoadingSettings(false)
    }
  }

  const saveRegistrationSettings = async () => {
    setIsSavingSettings(true)
    try {
      await apiClient.put("/settings/registration", {
        registrationCode: registrationCode.trim(),
      })
      toast({
        title: t("success"),
        description: "Registration code updated successfully",
      })
      setShowSettingsModal(false)
    } catch (error: any) {
      console.error("Error saving registration settings:", error)
      toast({
        title: t("error"),
        description: error.response?.data?.error || "Failed to save registration settings",
        variant: "destructive",
      })
    } finally {
      setIsSavingSettings(false)
    }
  }

  const handleApproveUser = async (userId: string) => {
    try {
      const result = await dispatch(approveUser(userId)).unwrap()
      if (result.success) {
        toast({
          title: t("success"),
          description: t("userApprovedMessage"),
        })
      }
    } catch (error: any) {
      const errorMessage = error?.message || error?.response?.data?.error || t("failedToApproveUser")
      toast({
        title: t("error"),
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  const [enableConfirm, setEnableConfirm] = useState<{ show: boolean; userId: string; userName: string }>({
    show: false,
    userId: "",
    userName: "",
  })
  const [isEnabling, setIsEnabling] = useState(false)

  const confirmEnableUser = async () => {
    setIsEnabling(true)
    try {
      await dispatch(enableUser(enableConfirm.userId)).unwrap()
      toast({
        title: t("success"),
        description: t("userEnabledMessage"),
      })
    } catch (error: any) {
      toast({
        title: t("error"),
        description: typeof error === "string" ? error : t("failedToEnableUser"),
        variant: "destructive",
      })
    } finally {
      setIsEnabling(false)
      setEnableConfirm({ show: false, userId: "", userName: "" })
    }
  }

  const handleDeleteUser = async (userId: string, userName: string) => {
    setDeleteConfirm({
      show: true,
      userId: userId,
      userName: userName,
    })
  }

  const confirmDeleteUser = async () => {
    try {
      const result = await dispatch(deleteUser(deleteConfirm.userId)).unwrap()
      if (result.success) {
        toast({
          title: t("success"),
          description: result.message || t("userDeletedMessage"),
        })
        // Refresh the users list after successful deletion
        dispatch(fetchUsers({ status: filter }))
      }
    } catch (error: any) {
      console.error("Delete user error:", error)
      let errorMessage = t("failedToDeleteUser")

      if (typeof error === "string") {
        errorMessage = error
      } else if (error?.message) {
        errorMessage = error.message
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      }

      toast({
        title: t("error"),
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setDeleteConfirm({ show: false, userId: "", userName: "" })
    }
  }

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]

      setPhotoError(null)

      try {
        const resizedFile = await validateAndResizeImage(file)
        setProfilePhoto(resizedFile)
        setPhotoPreview(URL.createObjectURL(resizedFile))
      } catch (error: any) {
        setPhotoError(error.message)
        setProfilePhoto(null)
        setPhotoPreview(null)
        e.target.value = ""
      }
    }
  }

  const handleEditUser = (user: UserType) => {
    setSelectedUser(user)
    setEditForm({
      fullName: user.fullName,
      email: user.email,
      building: user.building,
      roomNumber: user.roomNumber || "",
      phoneNumber: user.phoneNumber || "",
      role: user.role,
      canReceiveMessages: user.canReceiveMessages !== false, // Default to true if undefined
      muteGuestNotifications: user.muteGuestNotifications || false,
      disableEmailNotifications: user.disableEmailNotifications || false,
      managedBuildings: user.managedBuildings || [],
      managedTicketCategories: user.managedTicketCategories || [],
    })
    setPhotoPreview(user.profilePhotoUrl || null)
    setProfilePhoto(null)
    const softoneSelectValue = linkToSelectValue(softoneItems[user.id]?.link)
    setSoftoneValue(softoneSelectValue)
    setSoftoneInitialValue(softoneSelectValue)
    setShowEditModal(true)
    setSaveError(null)
    setPhotoError(null)
  }

  const handleManagedBuildingsChange = (buildingId: string) => {
    setEditForm((prevForm) => {
      const managedBuildings = prevForm.managedBuildings.includes(buildingId)
        ? prevForm.managedBuildings.filter((id) => id !== buildingId)
        : [...prevForm.managedBuildings, buildingId]
      return { ...prevForm, managedBuildings }
    })
  }

  const handleManagedTicketCategoriesChange = (category: string) => {
    setEditForm((prevForm) => {
      const managedTicketCategories = prevForm.managedTicketCategories.includes(category)
        ? prevForm.managedTicketCategories.filter((cat) => cat !== category)
        : [...prevForm.managedTicketCategories, category]
      return { ...prevForm, managedTicketCategories }
    })
  }

  const handleSaveEdit = async () => {
    setIsSaving(true)
    setSaveError(null)

    // Validate required fields
    if (!editForm.fullName.trim()) {
      setSaveError(t("requiredField") + ": " + t("fullName"))
      setIsSaving(false)
      return
    }

    if (!editForm.building || editForm.building.trim() === "" || editForm.building === "undefined") {
      setSaveError(t("requiredField") + ": " + t("building"))
      setIsSaving(false)
      return
    }

    if (!editForm.roomNumber.trim()) {
      setSaveError(t("requiredField") + ": " + t("roomNumber"))
      setIsSaving(false)
      return
    }

    try {
      // First update the role
      await dispatch(
        updateUserRole({
          userId: selectedUser!.id,
          role: editForm.role,
        }),
      ).unwrap()

      // Then update the profile details
      await dispatch(
        updateUserProfile({
          userId: selectedUser!.id,
          fullName: editForm.fullName,
          phoneNumber: editForm.phoneNumber,
          building: editForm.building,
          roomNumber: editForm.roomNumber,
          profilePhoto: profilePhoto || undefined,
          canReceiveMessages: editForm.canReceiveMessages,
          muteGuestNotifications: editForm.role === "admin" ? editForm.muteGuestNotifications : false,
          disableEmailNotifications: editForm.role === "admin" ? editForm.disableEmailNotifications : false,
          managedBuildings: editForm.role === "admin" ? editForm.managedBuildings : [],
          managedTicketCategories: editForm.role === "admin" ? editForm.managedTicketCategories : [],
        }),
      ).unwrap()

      // SoftOne link last, so its snapshot records the building/room number just saved
      if (softoneValue !== softoneInitialValue) {
        await saveSoftoneUserLink(selectedUser!.id, selectValueToChoice(softoneValue))
      }

      // Refresh the users list to get the updated data
      dispatch(fetchUsers({ status: filter }))
      loadSoftoneLinks()

      toast({
        title: t("success"),
        description: t("userUpdatedMessage"),
      })
      setShowEditModal(false)
    } catch (error: any) {
      // Extract the error message from the API response
      let errorMessage = t("failedToUpdateUser")

      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.message) {
        errorMessage = error.message
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (typeof error === "string") {
        errorMessage = error
      } else if (error?.payload) {
        // Handle Redux Toolkit error format
        errorMessage = error.payload.error || error.payload
      }

      setSaveError(errorMessage)

      toast({
        title: t("error"),
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleShowSettings = () => {
    setShowSettingsModal(true)
    loadRegistrationSettings()
  }

  // Same apartment registered by more than one active tenant, usually an old account left behind.
  // The apartment is the SoftOne one (link, else suggestion) so "A1" and "Α1" are the same; without SoftOne, the typed number.
  // ("duplicates" isn't a backend status, so the API returns every non-disabled user and the filtering happens here.)
  const apartmentKey = (user: UserType) => {
    const item = softoneItems[user.id]
    const prjc = item?.link?.status === "linked" ? item.link.softonePrjc : item?.suggestion?.softonePrjc
    return `${user.building}|${prjc ? `softone:${prjc}` : softoneSearchKey(user.roomNumber || "")}`
  }
  const tenantsPerApartment = new Map<string, number>()
  if (filter === "duplicates") {
    users
      .filter((user) => user.role !== "admin" && !user.isDisabled && (user.roomNumber || "").trim())
      .forEach((user) => {
        const key = apartmentKey(user)
        tenantsPerApartment.set(key, (tenantsPerApartment.get(key) || 0) + 1)
      })
  }
  const isDuplicate = (user: UserType) =>
    user.role !== "admin" && !user.isDisabled && (tenantsPerApartment.get(apartmentKey(user)) || 0) > 1

  const filteredUsers = users
    ? users.filter((user) => {
        const matchesBuilding = buildingFilter === "all" || user.building === buildingFilter
        const matchesSearch =
          user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.roomNumber || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (buildings.find((b) => b.id === user.building)?.name || user.building)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())

        const softoneItem = softoneItems[user.id]
        const matchesSoftone =
          softoneFilter === "all" ||
          (softoneFilter === "linked" && softoneItem?.link?.status === "linked") ||
          (softoneFilter === "none" && softoneItem?.link?.status === "none") ||
          (softoneFilter === "suggested" && !softoneItem?.link && !!softoneItem?.suggestion) ||
          (softoneFilter === "pending" && !softoneItem?.link && !softoneItem?.suggestion) ||
          (softoneFilter === "stale" && !!softoneItem?.stale)

        const matchesDuplicates = filter !== "duplicates" || isDuplicate(user)

        return matchesBuilding && matchesSearch && matchesSoftone && matchesDuplicates
      })
    : []

  const pendingUsersCount = users ? users.filter((user) => !user.isApproved).length : 0
  const filteredAdminUsersCount = filteredUsers ? filteredUsers.filter((user) => user.role === "admin").length : 0

  const handleSort = (field: "name" | "email" | "roomNumber") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const SortIcon = ({ field }: { field: "name" | "email" | "roomNumber" }) => {
    if (sortField !== field) return null
    return <span className="ms-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
  }

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    // duplicates: keep the users of one apartment next to each other
    if (!sortField && filter === "duplicates") return apartmentKey(a).localeCompare(apartmentKey(b))
    if (!sortField) return 0

    let aValue: string
    let bValue: string

    switch (sortField) {
      case "name":
        aValue = a.fullName.toLowerCase()
        bValue = b.fullName.toLowerCase()
        break
      case "email":
        aValue = a.email.toLowerCase()
        bValue = b.email.toLowerCase()
        break
      case "roomNumber":
        aValue = a.roomNumber || ""
        bValue = b.roomNumber || ""
        break
      default:
        return 0
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
    return 0
  })

  return (
    <Container fluid className="py-4 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
        <h1 className="fs-2 fw-bold mb-0 text-white">{t("userManagement")}</h1>
        <button className="btn-cancel-outline d-flex align-items-center" onClick={handleShowSettings}>
          <Settings size={16} className="me-2" />
          {t("registrationSettings") || "Registration Settings"}
        </button>
      </div>

      <Row className="mb-4 g-3">
        <Col md={6} xl={3} className="mb-3 mb-md-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex flex-column p-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted small fw-medium">{t("totalUsers")}</span>
                <User className="size-4 text-primary" />
              </div>
              <div className="fs-2 fw-bold text-gray-900">{filteredUsers.length}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} xl={3} className="mb-3 mb-md-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex flex-column p-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted small fw-medium">{t("adminUsers") || "Admin Users"}</span>
                <UserCheck className="size-4 text-primary" />
              </div>
              <div className="fs-2 fw-bold text-gray-900">{filteredAdminUsersCount}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} xl={3} className="mb-3 mb-md-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex flex-column p-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted small fw-medium">{t("pendingApprovals")}</span>
                <UserCheck className="size-4 text-primary" />
              </div>
              <div className="fs-2 fw-bold text-gray-900">{pendingUsersCount}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant={filter === "all" ? "primary" : "outline-primary"}
                onClick={() => setFilter("all")}
                className={filter !== "all" ? "border-2" : ""}
                size="sm"
                style={{ padding: "0.375rem 0.75rem" }}
              >
                {t("allUsers")}
              </Button>
              <Button
                variant={filter === "pending" ? "primary" : "outline-primary"}
                onClick={() => setFilter("pending")}
                className={filter !== "pending" ? "border-2" : ""}
                size="sm"
                style={{ padding: "0.375rem 0.75rem" }}
              >
                {t("pendingApprovals")}
              </Button>
              <Button
                variant={filter === "approved" ? "primary" : "outline-primary"}
                onClick={() => setFilter("approved")}
                className={filter !== "approved" ? "border-2" : ""}
                size="sm"
                style={{ padding: "0.375rem 0.75rem" }}
              >
                {t("approvedUsers")}
              </Button>
              <Button
                variant={filter === "disabled" ? "primary" : "outline-primary"}
                onClick={() => setFilter("disabled")}
                className={filter !== "disabled" ? "border-2" : ""}
                size="sm"
                style={{ padding: "0.375rem 0.75rem" }}
              >
                {t("disabledUsers")}
              </Button>
              <Button
                variant={filter === "duplicates" ? "primary" : "outline-primary"}
                onClick={() => setFilter("duplicates")}
                className={filter !== "duplicates" ? "border-2" : ""}
                size="sm"
                style={{ padding: "0.375rem 0.75rem" }}
                title={t("duplicateUsersHelp")}
              >
                {t("duplicateUsers")}
              </Button>
            </div>
          </div>

          <div className="d-flex flex-column flex-md-row gap-2 mb-3">
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" id="building-filter" size="sm">
                <Filter size={16} className="me-1" />
                {buildingFilter === "all"
                  ? t("allBuildings")
                  : buildings.find((b) => b.id === buildingFilter)?.name || buildingFilter}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setBuildingFilter("all")}>{t("allBuildings")}</Dropdown.Item>
                {buildings.map((building) => (
                  <Dropdown.Item key={building.id} onClick={() => setBuildingFilter(building.id)}>
                    {building.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" id="softone-filter" size="sm" disabled={softoneLoading}>
                <Filter size={16} className="me-1" />
                {softoneLoading ? t("softoneLoading") : t(softoneFilterLabels[softoneFilter])}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {(Object.keys(softoneFilterLabels) as SoftoneFilter[]).map((key) => (
                  <Dropdown.Item key={key} active={softoneFilter === key} onClick={() => setSoftoneFilter(key)}>
                    {t(softoneFilterLabels[key])}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <div className="position-relative flex-grow-1">
              <Form.Control
                type="text"
                placeholder={t("searchUsers")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ps-5"
                size="lg"
              />
              <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={18} />
            </div>
          </div>

          {softoneError && (
            <Alert variant="warning" className="py-2">
              {t("softoneLoadError")}: {softoneError}
            </Alert>
          )}

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" role="status">
                <span className="visually-hidden">{t("loading")}</span>
              </Spinner>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => handleSort("name")}
                      className="text-nowrap fw-semibold text-gray-700"
                    >
                      {t("name")}
                      <SortIcon field="name" />
                    </th>
                    <th
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => handleSort("email")}
                      className="text-nowrap fw-semibold text-gray-700"
                    >
                      {t("email")}
                      <SortIcon field="email" />
                    </th>
                    <th className="fw-semibold text-gray-700">{t("building")}</th>
                    <th
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => handleSort("roomNumber")}
                      className="text-nowrap fw-semibold text-gray-700"
                    >
                      {t("roomNumber")}
                      <SortIcon field="roomNumber" />
                    </th>
                    <th className="fw-semibold text-gray-700">{t("role")}</th>
                    <th className="fw-semibold text-gray-700">{t("managedBuildings")}</th>
                    <th className="fw-semibold text-gray-700">
                      {t("managedTicketCategories") || "Managed Ticket Categories"}
                    </th>
                    <th className="fw-semibold text-gray-700">{t("canReceiveMessages")}</th>
                    <th className="fw-semibold text-gray-700">{t("status")}</th>
                    <th className="fw-semibold text-gray-700">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-4 text-muted">
                        {t("noUsersFound")}
                      </td>
                    </tr>
                  ) : (
                    sortedUsers.map((user) => (
                      <tr key={user.id} className="border-bottom">
                        <td className="align-middle">
                          <div className="d-flex align-items-center">
                            {user.profilePhotoUrl ? (
                              <img
                                src={user.profilePhotoUrl || "/placeholder.svg"}
                                alt={user.fullName}
                                width={32}
                                height={32}
                                className="rounded-circle me-2"
                              />
                            ) : (
                              <div className="rounded-circle bg-primary bg-opacity-10 p-1 me-2">
                                <User size={20} className="text-primary" />
                              </div>
                            )}
                            {user.fullName}
                          </div>
                        </td>
                        <td className="align-middle">{user.email}</td>
                        <td className="align-middle">
                          {buildings.find((b) => b.id === user.building)?.name || user.building}
                        </td>
                        <td className="align-middle">
                          <div>{user.roomNumber}</div>
                          {/* SoftOne link under the apartment number, not in its own column */}
                          <div className="mt-1">
                          {softoneLoading && !softoneItems[user.id] ? (
                            <Spinner animation="border" size="sm" variant="secondary" />
                          ) : (
                            <SoftoneLinkBadge
                              link={softoneItems[user.id]?.link}
                              suggestion={softoneItems[user.id]?.suggestion}
                              note={softoneNote(softoneItems[user.id])}
                              warning={softoneItems[user.id]?.stale ? t("softoneStale") : undefined}
                              approving={approvingSoftoneUserId === user.id}
                              formatCode={softoneUnitNumber}
                              recordDetails={softoneApartmentDetails}
                              adminName={softoneAdminName}
                              onApproveSuggestion={
                                softoneItems[user.id]?.suggestion
                                  ? () =>
                                      handleApproveSoftoneSuggestion(
                                        user.id,
                                        softoneItems[user.id]!.suggestion!.softonePrjc,
                                      )
                                  : undefined
                              }
                            />
                          )}
                          </div>
                        </td>
                        <td className="align-middle">
                          <Badge bg={user.role === "admin" ? "primary" : "secondary"}>{t(user.role)}</Badge>
                        </td>
                        <td className="align-middle">
                          {user.role === "admin" && user.managedBuildings && user.managedBuildings.length > 0
                            ? user.managedBuildings
                                .map((buildingId) => buildings.find((b) => b.id === buildingId)?.name)
                                .filter((name): name is string => Boolean(name))
                                .sort(compareBuildingNames)
                                .join(", ")
                            : "-"}
                        </td>
                        <td className="align-middle">
                          {user.role === "admin" &&
                          user.managedTicketCategories &&
                          user.managedTicketCategories.length > 0
                            ? user.managedTicketCategories
                                .map(
                                  (category) => ticketCategories.find((tc) => tc.value === category)?.label || category,
                                )
                                .join(", ")
                            : "-"}
                        </td>
                        <td className="align-middle">
                          {user.role === "admin" ? (
                            <Badge bg={user.canReceiveMessages !== false ? "success" : "secondary"}>
                              {user.canReceiveMessages !== false ? t("yes") : t("no")}
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="align-middle">
                          {user.isDisabled ? (
                            <Badge bg="secondary">{t("disabled")}</Badge>
                          ) : (
                            <Badge bg={user.isApproved ? "success" : "warning"}>
                              {user.isApproved ? t("approved") : t("pending")}
                            </Badge>
                          )}
                        </td>
                        <td className="align-middle">
                          {user.isDisabled ? (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => setEnableConfirm({ show: true, userId: user.id, userName: user.fullName })}
                              title={t("enableUser")}
                            >
                              <UserPlus size={16} />
                            </Button>
                          ) : (
                          <div className="d-flex gap-2">
                            {!user.isApproved && (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleApproveUser(user.id)}
                                title={t("approveUser")}
                              >
                                <UserCheck size={16} />
                              </Button>
                            )}
                            <Button
                              className="admin-edit-btn"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              title={t("editUser")}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              className="admin-delete-btn"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id, user.fullName)}
                              title={t("deleteUser")}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showSettingsModal} onHide={() => setShowSettingsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("registrationSettings") || "Registration Settings"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                {t("registrationCode") || "Registration Code"} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value)}
                placeholder={t("enterRegistrationCode") || "Enter registration code"}
                disabled={isLoadingSettings}
              />
              <Form.Text className="text-muted">
                {t("registrationCodeAdminHelp") ||
                  "This code will be required for new user registrations. Code comparison is case-insensitive."}
              </Form.Text>
            </Form.Group>
          </Form>
          {isLoadingSettings && (
            <div className="text-center">
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="modal-cancel-btn" onClick={() => setShowSettingsModal(false)} disabled={isSavingSettings}>
            {t("cancel")}
          </Button>
          <Button
            className="modal-primary-btn"
            onClick={saveRegistrationSettings}
            disabled={isSavingSettings || !registrationCode.trim()}
          >
            {isSavingSettings ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t("saving")}
              </>
            ) : (
              t("save")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={enableConfirm.show}
        onHide={() => setEnableConfirm({ show: false, userId: "", userName: "" })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("enableUser")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("enableUserConfirmation", { name: enableConfirm.userName })}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="modal-cancel-btn"
            onClick={() => setEnableConfirm({ show: false, userId: "", userName: "" })}
          >
            {t("cancel")}
          </Button>
          <Button variant="success" onClick={confirmEnableUser} disabled={isEnabling}>
            {isEnabling ? <Spinner animation="border" size="sm" /> : t("enableUser")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={deleteConfirm.show}
        onHide={() => setDeleteConfirm({ show: false, userId: "", userName: "" })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("confirmDelete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("deleteUserConfirmation", { name: deleteConfirm.userName })}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="modal-cancel-btn"
            onClick={() => setDeleteConfirm({ show: false, userId: "", userName: "" })}
          >
            {t("cancel")}
          </Button>
          <Button className="modal-delete-btn" onClick={confirmDeleteUser}>
            {t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("editUser")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && (
            <Alert variant="danger" className="mb-3">
              {saveError}
            </Alert>
          )}

          <Form>
            <div className="d-flex flex-column align-items-center mb-4">
              <div className="position-relative mb-3" style={{ width: "96px", height: "96px" }}>
                {photoPreview ? (
                  <img
                    src={photoPreview || "/placeholder.svg"}
                    alt="Profile Preview"
                    className="rounded-circle w-100 h-100 object-fit-cover"
                  />
                ) : (
                  <div className="rounded-circle bg-light d-flex align-items-center justify-content-center w-100 h-100">
                    <User className="size-8 text-muted" />
                  </div>
                )}
              </div>
              <Form.Control
                id="profilePhoto"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="d-none"
              />
              <>
                <Form.Label htmlFor="profilePhoto" className="btn btn-primary btn-sm cursor-pointer">
                  <Upload className="size-4 me-1" />
                  {t("uploadPhoto")}
                </Form.Label>
                <div className="text-muted small text-center mt-1">
                  Will be resized to {maxDimension}x{maxDimension}px
                </div>
                {photoError && <div className="text-danger small text-center mt-1">{photoError}</div>}
              </>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>
                {t("fullName")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={editForm.fullName}
                onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                required
                isInvalid={!editForm.fullName.trim()}
              />
              {!editForm.fullName.trim() && (
                <Form.Control.Feedback type="invalid">{t("requiredField")}</Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("email")}</Form.Label>
              <Form.Control
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                disabled
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("phoneNumber")}</Form.Label>
              <Form.Control
                type="tel"
                value={editForm.phoneNumber}
                onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {t("building")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={editForm.building}
                onChange={(e) => {
                  setEditForm({ ...editForm, building: e.target.value })
                  // an apartment of the previous building can't stay selected
                  if (softoneValue && softoneValue !== "none") setSoftoneValue("")
                }}
                required
                isInvalid={!editForm.building || editForm.building === "undefined"}
              >
                <option value="">{t("selectBuilding")}</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </Form.Select>
              {!editForm.building && <Form.Control.Feedback type="invalid">{t("requiredField")}</Form.Control.Feedback>}
              {buildingsLoading && (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" variant="primary" role="status">
                    <span className="visually-hidden">{t("loading")}</span>
                  </Spinner>
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {t("roomNumber")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={editForm.roomNumber}
                onChange={(e) => setEditForm({ ...editForm, roomNumber: e.target.value })}
                required
                isInvalid={!editForm.roomNumber.trim()}
              />
              {!editForm.roomNumber.trim() && (
                <Form.Control.Feedback type="invalid">{t("requiredField")}</Form.Control.Feedback>
              )}
            </Form.Group>
            {selectedUser && (
              <SoftoneLinkSelect
                value={softoneValue}
                onChange={setSoftoneValue}
                options={softoneApartmentOptions}
                // the suggestion was computed for the saved building; hide it once the admin picks another one
                suggestion={
                  editForm.building === selectedUser.building ? softoneItems[selectedUser.id]?.suggestion : null
                }
                note={
                  !softoneBuildingLinks[editForm.building]
                    ? t("softoneBuildingNotLinked")
                    : softoneNote(softoneItems[selectedUser.id])
                }
                warning={softoneItems[selectedUser.id]?.stale ? t("softoneStale") : undefined}
                loading={softoneLoading}
                formatCode={softoneUnitNumber}
                disabled={!!softoneError}
              />
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t("role")}</Form.Label>
              <Form.Select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                <option value="user">{t("user")}</option>
                <option value="admin">{t("admin")}</option>
              </Form.Select>
            </Form.Group>
            {editForm.role === "admin" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="canReceiveMessages"
                    label={t("canReceiveMessages")}
                    checked={editForm.canReceiveMessages}
                    onChange={(e) => setEditForm({ ...editForm, canReceiveMessages: e.target.checked })}
                  />
                  <Form.Text className="text-muted">{t("canReceiveMessagesHelp")}</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="muteGuestNotifications"
                    label={t("muteGuestNotifications")}
                    checked={editForm.muteGuestNotifications}
                    onChange={(e) => setEditForm({ ...editForm, muteGuestNotifications: e.target.checked })}
                  />
                  <Form.Text className="text-muted">{t("muteGuestNotificationsHelp")}</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="disableEmailNotifications"
                    label={t("disableEmailNotifications")}
                    checked={editForm.disableEmailNotifications}
                    onChange={(e) => setEditForm({ ...editForm, disableEmailNotifications: e.target.checked })}
                  />
                  <Form.Text className="text-muted">{t("disableEmailNotificationsHelp")}</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t("managedBuildings")}</Form.Label>
                  <div className="p-2 border rounded" style={{ maxHeight: "150px", overflowY: "auto" }}>
                    {buildingsLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : (
                      buildings.map((building) => (
                        <Form.Check
                          key={building.id}
                          type="checkbox"
                          id={`building-${building.id}`}
                          label={building.name}
                          checked={editForm.managedBuildings.includes(building.id)}
                          onChange={() => handleManagedBuildingsChange(building.id)}
                        />
                      ))
                    )}
                  </div>
                  <Form.Text className="text-muted">{t("managedBuildingsHelp")}</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t("managedTicketCategories") || "Managed Ticket Categories"}</Form.Label>
                  <div className="p-2 border rounded" style={{ maxHeight: "150px", overflowY: "auto" }}>
                    {ticketCategories.map((category) => (
                      <Form.Check
                        key={category.value}
                        type="checkbox"
                        id={`category-${category.value}`}
                        label={category.label}
                        checked={editForm.managedTicketCategories.includes(category.value)}
                        onChange={() => handleManagedTicketCategoriesChange(category.value)}
                      />
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    {t("managedTicketCategoriesHelp") ||
                      "Leave empty to allow access to all ticket categories. Select specific categories to restrict access."}
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button className="modal-cancel-btn" onClick={() => setShowEditModal(false)} disabled={isSaving}>
            {t("cancel")}
          </Button>
          <Button className="modal-primary-btn" onClick={handleSaveEdit} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" role="status" aria-hidden="true" />
                {t("saving")}
              </>
            ) : (
              t("save")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
